import BaseChartCard from 'common/components/BaseChartCard'
import { useGetWaterQualityLineChart } from 'modules/water/api/queries'
import { WaterQualityChart } from 'modules/water/api/types'
import { PeriodOfDay, QualityOption, WATER_QUALITY_OPTIONS } from 'modules/water/constants'
import { getMinMaxValueFromSeries, getSeries } from 'modules/water/utils/chart'
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { CategoryButtonGroup } from '../CategoryButtonGroup'
import LineChart from '../LineChart'

const range = {
  [QualityOption.PH]: [7.5, 8.6],
  [QualityOption.Alkali]: [50, 400],
  [QualityOption.Temperature]: [20, 35],
  [QualityOption.Nitrite]: [0, 5],
  [QualityOption.DO]: [2, 10],
  [QualityOption.Ammonia]: [0, 5],
  [QualityOption.Terbidity]: [0, 150],
  [QualityOption.Salinity]: [0, 40],
}

const QualityChart = () => {
  const { t } = useTranslation('water')
  const theme = useTheme()
  const [min, setMin] = useState(0)
  const [max, setMax] = useState(0)

  const [filter, setFilter] = useState<QualityOption>(QualityOption.PH)
  const { data, isFetching, refetch } = useGetWaterQualityLineChart()

  const morning = (data ?? []).filter((el) => el.timeOfDay === PeriodOfDay.Morning)
  const evening = (data ?? []).filter((el) => el.timeOfDay === PeriodOfDay.Evening)

  const morningSeries = getSeries<WaterQualityChart>(QualityOption, morning)
  const eveningSeries = getSeries<WaterQualityChart>(QualityOption, evening)

  const { min: minMorning, max: maxMorning } = getMinMaxValueFromSeries(morningSeries[filter])
  const { min: minEvening, max: maxEvening } = getMinMaxValueFromSeries(eveningSeries[filter])

  useEffect(() => {
    let minDefault = range[filter][0] ?? 0
    let maxDefault = range[filter][1] ?? 0

    if (minMorning !== undefined && maxMorning !== undefined) {
      if (minMorning < minDefault) {
        minDefault = minMorning
      }
      if (maxMorning > maxDefault) {
        maxDefault = maxMorning
      }
    }

    if (minEvening !== undefined && maxEvening !== undefined) {
      if (minEvening < minDefault) {
        minDefault = minEvening
      }
      if (maxEvening > maxDefault) {
        maxDefault = maxEvening
      }
    }

    setMin(minDefault)
    setMax(maxDefault)
  }, [filter, minMorning, minEvening, maxMorning, maxEvening])

  const unit = {
    [QualityOption.PH]: '',
    [QualityOption.Alkali]: '(ppm)',
    [QualityOption.Temperature]: '(°C)',
    [QualityOption.Nitrite]: '(ppm)',
    [QualityOption.DO]: '(ppm)',
    [QualityOption.Ammonia]: '(ppm)',
    [QualityOption.Terbidity]: '(NTU)',
    [QualityOption.Salinity]: '(ppt)',
  }

  const chartData = [
    {
      id: PeriodOfDay.Morning,
      name: t(PeriodOfDay.Morning),
      data: morningSeries[filter],
    },
    {
      id: PeriodOfDay.Evening,
      name: t(PeriodOfDay.Evening),
      data: eveningSeries[filter],
    },
  ]

  const handleChange = useCallback((value: QualityOption) => {
    setFilter(value)
  }, [])

  return (
    <BaseChartCard title={t('water.quality.today')} refresh={refetch}>
      <Stack direction="row" spacing={1} sx={{ overflowX: 'scroll', py: 2, pl: 2 }}>
        <CategoryButtonGroup
          options={WATER_QUALITY_OPTIONS.map((el) => ({
            label: t(el.label),
            value: el.category,
          }))}
          selected={filter}
          onChange={handleChange}
        />
      </Stack>
      <LineChart
        min={min}
        max={max}
        yTitle={`${t('value')} ${unit[filter]}`}
        yData={chartData}
        isLoading={isFetching}
        options={{ colors: [theme.palette.warning.main, theme.palette.info.dark] }}
        dataLength={data?.length || 0}
      />
    </BaseChartCard>
  )
}

export default QualityChart
